import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import { FulfillmentActionSheet } from '@/components/partners/fulfillment-action-sheet';
import { FulfillmentName } from '@/types';
import { Text, TouchableOpacity, View } from 'react-native';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useNewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';

export const FulfillmentPill: React.FC = () => {
  const {
    allowDeliveryDateEdit,
    draftOrder,
    hasResponse,
    selectableFulfillmentOptions,
  } = useNewOrderContext();
  const { selectedFulfillment, setNextFulfillment, updateAndClearQuantity } =
    useNewOrderActionsContext();
  const { isUpdatingQuantity } = useNewOrderLoadingContext();
  const orderAvailability = useSupplierOrderAvailability();
  const [styles] = useStyles();

  const [showFulfillmentSelector, setShowFulfillmentSelector] = useState(false);

  const fulfillmentNameText = useMemo(() => {
    if (!selectedFulfillment) {
      return null;
    }

    const displayName = orderAvailability.fulfillmentDisplayName;

    return translateFulfillmentType(
      selectedFulfillment?.type || FulfillmentName.Delivery,
      'fulfillment_pill',
      displayName,
      {
        date: orderAvailability.getSelectedFulfillmentDateTimeString(
          selectedFulfillment.date,
          selectedFulfillment.timeslot,
        ),
      },
    );
  }, [selectedFulfillment, orderAvailability]);

  const cutoffAndMinimumText = useMemo(() => {
    const cutoffText = orderAvailability.getCutoffForDate(
      selectedFulfillment?.date,
      true,
    );
    const minimumText = orderAvailability.getOrderMinDisplayStringForDate(
      selectedFulfillment?.date,
    );

    if (cutoffText && minimumText) {
      return i18n.t('fulfillment_pill.order_minimum_with_cutoff', {
        cutoff: cutoffText,
        minimum: minimumText,
      });
    } else if (cutoffText) {
      return i18n.t('fulfillment_pill.order_cutoff', {
        cutoff: cutoffText,
      });
    } else if (minimumText) {
      return i18n.t('fulfillment_pill.order_minimum', {
        minimum: minimumText,
      });
    } else {
      return null;
    }
  }, [orderAvailability, selectedFulfillment]);

  if (!selectedFulfillment || !hasResponse) {
    return null;
  }

  return (
    <>
      <View style={styles.base}>
        <TouchableOpacity
          disabled={!allowDeliveryDateEdit}
          onPress={() => {
            Analytics.track('fulfillment_pill.edit_button.press');
            setShowFulfillmentSelector(true);
          }}
          style={styles.button}
          testID="fulfillment-pill"
        >
          <Text
            style={[TYPOGRAPHY.small, FONTS.primarySemiBold, styles.dateText]}
          >
            {fulfillmentNameText}
          </Text>
          {cutoffAndMinimumText ? (
            <Text style={[TYPOGRAPHY.xsmall, styles.dateText]}>
              {cutoffAndMinimumText}
            </Text>
          ) : null}
        </TouchableOpacity>
      </View>
      {showFulfillmentSelector ? (
        <FulfillmentActionSheet
          draftOrder={draftOrder}
          fulfillmentOptions={selectableFulfillmentOptions}
          initialSelectedFulfillment={selectedFulfillment!}
          isLoading={isUpdatingQuantity}
          onClose={() => setShowFulfillmentSelector(false)}
          onOpen={() => updateAndClearQuantity('fulfillment_action_sheet')}
          onSave={setNextFulfillment}
          trackingProperties={{
            source: 'order_guide',
          }}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    marginTop: 12,
  },
  button: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderRadius: 40,
    justifyContent: 'center',
    minHeight: 32,
    paddingHorizontal: 16,
    paddingVertical: 4,
    ...shadow({
      elevation: 1,
      opacity: 0.15,
      radius: 6,
      x: 0,
      y: 4,
    }),
  },
  dateText: {
    textAlign: 'center',
  },
}));
